<template>
  <div
    :data-testid="dataTestid"
    role="group"
    :aria-label="ariaLabel"
    class="flex w-full justify-center gap-2"
  >
    <!-- No maxlength here on purpose: WebKit clamps one-time-code autofill to maxlength
    before the input event ever fires, so a per-box maxlength="1" silently truncates SMS
    autofill on iOS to a single character. The single-digit-per-box display is enforced
    in JS (handleInput) instead, which still sees the full autofilled code to distribute. -->
    <input
      v-for="(_, index) in length"
      :key="index"
      :ref="el => setInputRef(el, index)"
      v-model="digits[index]"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      pattern="[0-9]*"
      :aria-label="`Digit ${index + 1} of ${length}`"
      :aria-invalid="!!error"
      :disabled="disabled"
      class="h-[3.25rem] max-w-[3.25rem] min-w-0 flex-1 rounded border bg-semantic-surface-default text-center text-lg font-semibold text-semantic-surface-on-surface outline-none transition-colors focus-visible:outline-none disabled:bg-semantic-states-disabled-default disabled:text-semantic-states-disabled-on-default"
      :class="inputStateClass"
      @input="handleInput($event, index)"
      @keydown="handleKeydown($event, index)"
      @paste="handlePaste($event, index)"
      @focus="handleFocus(index)"
    />
  </div>
</template>

<script setup>
defineOptions({
  name: 'V2AtomsOtpInput'
})

const props = defineProps({
  length: {
    type: Number,
    default: 6
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Accepts a Boolean (just flip the border red) or a String (also read out via aria-invalid
  // for screen readers) — the field itself has no room to render the message.
  error: {
    type: [Boolean, String],
    default: false
  },
  autoFocus: {
    type: Boolean,
    default: true
  },
  ariaLabel: {
    type: String,
    default: 'One-time passcode'
  },
  dataTestid: {
    type: String,
    default: 'v2-atoms-otp-input'
  }
})

const emit = defineEmits(['update:modelValue', 'complete'])

const modelValue = defineModel({
  type: String,
  default: ''
})

const inputRefs = ref([])
const digits = ref(splitValue(modelValue.value))

function setInputRef (el, index) {
  inputRefs.value[index] = el
}

function splitValue (value) {
  const chars = String(value || '').replace(/\D/g, '').split('')
  return Array.from({ length: props.length }, (_, i) => chars[i] || '')
}

function focusInput (index) {
  const target = inputRefs.value[Math.max(0, Math.min(index, props.length - 1))]
  target?.focus()
  target?.select?.()
}

function emitValue () {
  const value = digits.value.join('')
  emit('update:modelValue', value)
  modelValue.value = value

  // Note: don't check the joined string for emptiness (e.g. `value.includes('')`) — an
  // empty string is trivially "included" in every string, so that check can never be false.
  if (digits.value.every(digit => digit !== '')) {
    emit('complete', value)
  }
}

function handleInput (event, index) {
  const raw = event.target.value
  const rawDigits = raw.replace(/\D/g, '')

  // Autofill (mobile SMS suggestion, password managers) can drop the full code into
  // whichever box has focus without respecting maxlength="1" — the input event then
  // carries every digit here rather than just one, so distribute it like a paste
  // instead of collapsing it down to a single character.
  if (rawDigits.length > 1) {
    distributeDigits(rawDigits, index)
    event.target.value = digits.value[index]
    return
  }

  digits.value[index] = rawDigits
  event.target.value = rawDigits

  if (rawDigits && index < props.length - 1) {
    focusInput(index + 1)
  }

  emitValue()
}

function handleKeydown (event, index) {
  // Let browser/OS shortcuts (copy, paste, refresh, devtools, tab) through untouched.
  if (event.metaKey || event.ctrlKey || event.altKey) return

  switch (event.key) {
    case 'Backspace':
      if (digits.value[index]) {
        digits.value[index] = ''
        emitValue()
      } else if (index > 0) {
        digits.value[index - 1] = ''
        emitValue()
        focusInput(index - 1)
      }
      event.preventDefault()
      break
    case 'Delete':
      digits.value[index] = ''
      emitValue()
      event.preventDefault()
      break
    case 'ArrowLeft':
      focusInput(index - 1)
      event.preventDefault()
      break
    case 'ArrowRight':
      focusInput(index + 1)
      event.preventDefault()
      break
    default:
      // Reject anything that isn't a single digit — keeps letters/symbols from ever
      // reaching the field instead of relying on inputmode alone (desktop keyboards ignore it).
      if (event.key.length === 1 && !/\d/.test(event.key)) {
        event.preventDefault()
      }
  }
}

function distributeDigits (raw, index) {
  const pastedDigits = raw.replace(/\D/g, '').split('')
  if (!pastedDigits.length) return

  // A full-code fill (paste, password manager, or SMS autofill) always fills from the
  // start regardless of which box triggered it — that's what every native OTP field does.
  const startIndex = pastedDigits.length >= props.length ? 0 : index

  pastedDigits.forEach((digit, offset) => {
    const target = startIndex + offset
    if (target < props.length) {
      digits.value[target] = digit
    }
  })

  emitValue()
  const nextEmptyIndex = digits.value.findIndex(d => !d)
  focusInput(nextEmptyIndex === -1 ? props.length - 1 : nextEmptyIndex)
}

function handlePaste (event, index) {
  event.preventDefault()
  const pasted = (event.clipboardData || window.clipboardData).getData('text')
  distributeDigits(pasted, index)
}

function handleFocus (index) {
  inputRefs.value[index]?.select?.()
}

watch(modelValue, value => {
  const next = splitValue(value)
  if (next.join('') === digits.value.join('')) return
  digits.value = next
})

watch(() => props.length, () => {
  digits.value = splitValue(modelValue.value)
})

function focus () {
  const nextEmptyIndex = digits.value.findIndex(d => !d)
  focusInput(nextEmptyIndex === -1 ? 0 : nextEmptyIndex)
}

function clear () {
  digits.value = splitValue('')
  emitValue()
  focusInput(0)
}

defineExpose({ focus, clear })

const inputStateClass = computed(() => {
  if (props.disabled) return 'border-semantic-outline-variant'
  if (props.error) return 'border-semantic-states-error-default'
  return 'border-semantic-outline-variant focus:border-semantic-primary-default focus:ring-2 focus:ring-semantic-primary-container-high'
})

onMounted(() => {
  if (props.autoFocus) focusInput(0)
})
</script>
